import React, { Component, useCallback, useEffect } from 'react';

import { NodeGraph } from '@/components/nodeGraph/NodeGraph';
import { queryClient } from '@/queryclient';
import { ResourceType } from '@/types';
import { QueryClientProvider } from '@/utils/api';
import { useStorage } from '@/utils/storage/useStorage.util';

import { getDefaultFilters, getQueryKey, getStorageKey } from './constants';
import type { Filters, GraphEntityType } from './types';
import { isSeedFilter } from './types';

// Error Boundary component (can be extracted later to shared components)
class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Use proper error tracking in production
    console.error('GraphExplorer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto max-w-2xl p-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <h3 className="mb-2 font-semibold text-red-800">
              Unable to load Graph Explorer
            </h3>
            <p className="mb-3 text-sm text-red-700">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <details className="text-xs">
              <summary className="cursor-pointer text-red-600 hover:text-red-800">
                Show technical details
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-red-100 p-2 text-red-800">
                {this.state.error?.stack}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * GraphExplorer Component
 *
 * Main component for the Graph Explorer feature within Insights.
 * Provides an interactive graph visualization of assets, seeds, and their relationships.
 *
 * Features:
 * - Dynamic entity type switching (Assets, Seeds, Risks, etc.)
 * - Entity-specific filtering (delegates to AssetTableFilters or SeedSearchFilters)
 * - Graph rendering via NodeGraph component
 * - Persistent state management (filters, cluster settings)
 *
 * State Management:
 * - clusterByEntity: Persisted in localStorage, determines graph clustering strategy
 * - filters: Entity-specific filters, persisted separately for each entity type
 *
 * Integration:
 * - Used by: /insights page as a tab content component
 * - Depends on: NodeGraph component for graph rendering
 */
export const GraphExplorer: React.FC = () => {
  // State for cluster by entity selection - persisted in localStorage
  const [clusterByEntity, setClusterByEntityRaw] = useStorage<GraphEntityType>(
    {
      key: 'graphExplorer.clusterByEntity',
      queryKey: 'clusterByEntity',
    },
    ResourceType.Asset
  );

  // Get storage configuration for current entity type
  const storageKey = getStorageKey(clusterByEntity);
  const queryKey = getQueryKey(clusterByEntity);
  const defaultFilters = getDefaultFilters(clusterByEntity);

  // Use dynamic storage key based on entity type
  const [filters, setFilters] = useStorage<Filters>(
    {
      queryKey,
      key: storageKey,
      ephemeralKeys: ['search'],
    },
    defaultFilters
  );

  // Handle entity type changes - clear incompatible filters when switching
  const setClusterByEntity = useCallback(
    (newEntity: GraphEntityType) => {
      if (newEntity !== clusterByEntity) {
        // Clear filters when switching entity types to avoid incompatibility
        setFilters(getDefaultFilters(newEntity));
      }
      setClusterByEntityRaw(newEntity);
    },
    [clusterByEntity, setClusterByEntityRaw, setFilters]
  );

  // Validate filter compatibility with current entity type
  // If filters don't match entity type (e.g., seed filters with asset entity), reset to defaults
  useEffect(() => {
    const isSeedEntity = clusterByEntity === ResourceType.Seed;
    const hasSeedFilters = isSeedFilter(filters);

    if (isSeedEntity !== hasSeedFilters) {
      // Filter type mismatch - reset to defaults
      setFilters(defaultFilters);
    }
  }, [clusterByEntity, filters, defaultFilters, setFilters]);

  return (
    <div className="bg-chariot-background relative flex size-full min-h-[500px] flex-col overflow-hidden">
      {/* Graph Content */}
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <div className="relative size-full">
            <NodeGraph
              clusterByEntity={clusterByEntity}
              setClusterByEntity={setClusterByEntity}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
};

GraphExplorer.displayName = 'GraphExplorer';
